"use client";
import { memo, useMemo } from "react";
import type { FC, ReactNode } from "react";
import type { UiState } from "instantsearch.js";
import { Configure, useRefinementList } from "react-instantsearch";
import { InstantSearchNext } from "react-instantsearch-nextjs";
import type { InstantSearchNextRouting } from "react-instantsearch-nextjs";
import { useAlgoliaSearchClient } from "@lib/wrappers/algolia/algoliaSearchClient";
import { getAlgoliaUserToken } from "@lib/analytics/getAlgoliaUserToken";
import { AND_SYMBOL } from "@lib/constants/filtersConstants";
import { useStore } from "@store/index";
import { Box } from "@ecco/ui/box";

const RESERVED_ROUTE_KEYS = ["query", "sortBy", "page"];
const DEFAULT_MIN_BLOCK_SIZE = "25vh";

type RouteState = Record<string, string | string[] | number | undefined>;

type AlgoliaProviderProps = {
    children: ReactNode;
    locale: string;
    mode?: "products" | "stores";
    aroundLatLng?: string;
    facetFilters?: string[];
    hitsPerPage?: number;
    minBlockSize?: string | number;
    facetAttributes?: string[];
};

const normalizeFacetAttributes = (facetAttributes: string[] = []) => {
    return Array.from(new Set(facetAttributes.filter(Boolean))).sort();
};

const getRouteParamValues = (value: RouteState[string]): string[] => {
    if (Array.isArray(value)) {
        return value.flatMap((item) => item.split(AND_SYMBOL).filter(Boolean));
    }

    if (typeof value === "string") {
        return value.split(AND_SYMBOL).filter(Boolean);
    }

    return [];
};

const getSortSlug = (sortBy: unknown, indexName: string) => {
    if (typeof sortBy !== "string" || sortBy === indexName) return undefined;

    const prefixedSort = `${indexName}_`;
    return sortBy.startsWith(prefixedSort) ? sortBy.slice(prefixedSort.length) : undefined;
};

// The real filter UI loads late (ssr:false), so this invisible widget makes
// InstantSearch pick up filters from the URL right away instead of dropping them.
const VirtualRefinementList = memo(({ attribute }: { attribute: string }) => {
    useRefinementList({ attribute, operator: "or", limit: 100 });
    return null;
});

VirtualRefinementList.displayName = "VirtualRefinementList";

export const AlgoliaProvider: FC<AlgoliaProviderProps> = ({
    children,
    locale,
    mode,
    facetFilters,
    aroundLatLng,
    hitsPerPage,
    minBlockSize = DEFAULT_MIN_BLOCK_SIZE,
    facetAttributes = [],
}) => {
    const { indexName, searchClient } = useAlgoliaSearchClient(locale, mode);
    const me = useStore((state) => state.data.me);
    const algoliaUserToken = getAlgoliaUserToken(me);

    const facetAttributesKey = facetAttributes.join("|");
    const ownedFacetAttributes = useMemo(
        () => normalizeFacetAttributes(facetAttributes),
        [facetAttributesKey]
    );
    const instantSearchKey = `${indexName}:${ownedFacetAttributes.join("|")}`;

    const routing = useMemo<InstantSearchNextRouting<UiState, RouteState>>(() => {
        const ownedRouteKeys = new Set([...RESERVED_ROUTE_KEYS, ...ownedFacetAttributes]);
        const ownedFacetSet = new Set(ownedFacetAttributes);

        return {
            stateMapping: {
                stateToRoute(uiState) {
                    const indexUiState = uiState[indexName] ?? {};
                    const route: RouteState = {};

                    if (indexUiState.query) {
                        route.query = indexUiState.query;
                    }

                    const sortSlug = getSortSlug(indexUiState.sortBy, indexName);
                    if (sortSlug) {
                        route.sortBy = sortSlug;
                    }

                    if (indexUiState.page) {
                        route.page = indexUiState.page;
                    }

                    Object.entries(indexUiState.refinementList ?? {}).forEach(
                        ([facetName, refinements]) => {
                            if (!ownedFacetSet.has(facetName) || !refinements?.length) return;
                            route[facetName] = refinements.join(AND_SYMBOL);
                        }
                    );

                    // Carry over query params we don't own (utm, gclid, etc.) so
                    // InstantSearch doesn't strip them when it rewrites the URL.
                    if (typeof window !== "undefined") {
                        const currentSearchParams = new URLSearchParams(window.location.search);

                        currentSearchParams.forEach((value, key) => {
                            if (!ownedRouteKeys.has(key) && route[key] === undefined) {
                                route[key] = value;
                            }
                        });
                    }

                    return route;
                },

                routeToState(routeState) {
                    const refinementList: Record<string, string[]> = {};

                    ownedFacetAttributes.forEach((facetName) => {
                        const values = getRouteParamValues(routeState[facetName]);
                        if (values.length) {
                            refinementList[facetName] = values;
                        }
                    });

                    const query =
                        typeof routeState.query === "string" ? routeState.query : undefined;
                    const pageCandidate =
                        typeof routeState.page === "number"
                            ? routeState.page
                            : typeof routeState.page === "string"
                              ? Number(routeState.page)
                              : NaN;
                    const page =
                        Number.isInteger(pageCandidate) && pageCandidate > 0
                            ? pageCandidate
                            : undefined;
                    const sortSlug =
                        typeof routeState.sortBy === "string" ? routeState.sortBy : undefined;

                    return {
                        [indexName]: {
                            query,
                            page,
                            sortBy: sortSlug ? `${indexName}_${sortSlug}` : undefined,
                            refinementList,
                        },
                    };
                },
            },
        };
    }, [indexName, ownedFacetAttributes]);

    return (
        <Box minBlockSize={minBlockSize}>
            <InstantSearchNext<UiState, RouteState>
                key={instantSearchKey}
                searchClient={searchClient}
                indexName={indexName}
                routing={routing}
                future={{ preserveSharedStateOnUnmount: true }}
            >
                {ownedFacetAttributes.map((attribute) => (
                    <VirtualRefinementList key={attribute} attribute={attribute} />
                ))}
                <Configure
                    hitsPerPage={hitsPerPage}
                    aroundLatLng={aroundLatLng}
                    clickAnalytics
                    facetFilters={facetFilters ?? ["hasStock:true"]}
                    userToken={algoliaUserToken}
                />
                {children}
            </InstantSearchNext>
        </Box>
    );
};

export default AlgoliaProvider;
